/**
 * How a head-to-head sport's match READS — the words for its periods, its
 * extra time and its shoot-out, its group points, and its win methods' names.
 * One table, so OVR's board, the results feed and the portal's results pages
 * say the same thing (before this, the portal called every period a "Game",
 * every extra time "AET" and every shoot-out "Pens", and gave 3 points a win
 * in every sport).
 *
 * Keyed by the SEAG `sports.code`, with a discipline override where a
 * discipline plays differently (beach volleyball, 3x3, snooker vs pool).
 * Discipline codes are both the programme seed's and the ones already in the
 * dev database (VBB / VBI). Values follow the predecessor OVR (WSL-OVR-X, the
 * SUKMA 2026 screens) unless a line says otherwise; `CONFIRM` marks a number
 * to check against the SEA Games technical regulations.
 */

/** What one period of the match is called. */
export const MATCH_PERIOD_WORDS = ["SET", "GAME", "QUARTER", "HALF", "PERIOD", "END", "FRAME", "RACK", "ROUND", "INNINGS", "LEG", "RACE"] as const;
export type MatchPeriodWord = (typeof MATCH_PERIOD_WORDS)[number];

/** Extra play after a level regulation score: OT overtime (basketball, ice hockey, netball) · ET extra time (football, rugby). */
export const MATCH_EXTRA_WORDS = ["OT", "ET"] as const;
export type MatchExtraWord = (typeof MATCH_EXTRA_WORDS)[number];

/** A tie decider: PSO penalty shoot-out (hockey) · PENS penalties (football) · SO shootout (ice hockey) · SUPER_OVER (cricket). */
export const MATCH_SHOOTOUT_WORDS = ["PSO", "PENS", "SO", "SUPER_OVER"] as const;
export type MatchShootoutWord = (typeof MATCH_SHOOTOUT_WORDS)[number];

/**
 * Group-table points. `forfeit` is the loser's points when it lost by an
 * invalid result (WO, DNS…; absent = `loss`). `extra_win` / `extra_loss`
 * are a win / loss decided in overtime or a shoot-out (absent = `win` /
 * `loss`). `by_sets`, when present, replaces `win` / `loss`: points by the
 * match's sets / games score, keyed "won-lost" (volleyball 3-2 → 2).
 * `no_result` is each side's points for an abandoned match (cricket).
 */
export interface MatchPoints {
	readonly win: number;
	readonly draw: number;
	readonly loss: number;
	readonly forfeit?: number;
	readonly extra_win?: number;
	readonly extra_loss?: number;
	readonly by_sets?: Readonly<Record<string, number>>;
	readonly no_result?: number;
}

/**
 * How a group table separates sides level on points.
 *
 *   SCORE  score difference, then score for (goals, sets, games), then the
 *          referee's order — every sport without a rule of its own
 *   BWF    BWF General Competition Regulations, group play (the Olympic
 *          tournament's rule; team events by the Thomas & Uber Cup rule):
 *            two level → the winner of their match;
 *            three or more level → difference of matches (a tie's rubbers;
 *            a match's games), then of games, then of points (rallies);
 *            two still level after that → the winner of their match;
 *            otherwise the referee's order (drawing of lots)
 */
export const GROUP_TIEBREAKS = ["SCORE", "BWF"] as const;
export type GroupTiebreak = (typeof GROUP_TIEBREAKS)[number];

export interface MatchFormat {
	readonly period: MatchPeriodWord;
	/** Absent = SCORE. */
	readonly tiebreak?: GroupTiebreak;
	readonly extra?: MatchExtraWord;
	readonly shootout?: MatchShootoutWord;
	/**
	 * The extra rows' own names, in order, where the sport's extra play is
	 * not "extra time n": kabaddi's five raids each (5R), then the golden
	 * raid (GR). Both count in the total, as the predecessor's did.
	 */
	readonly extra_labels?: readonly string[];
	/** Absent: the sport's group tables are not points tables (or not known yet) — the portal then counts wins. */
	readonly points?: MatchPoints;
}

const SETS_3_0 = { "3-0": 3, "3-1": 3, "3-2": 2, "2-3": 1, "1-3": 0, "0-3": 0 } as const;
const SETS_2_0 = { "2-0": 3, "2-1": 2, "1-2": 1, "0-2": 0 } as const;

/**
 * By sport. Where it comes from:
 *   BD  game, 1 point a tie won (BWF team events)            BKB quarter, OT; W 2 L 1 forfeit 0 (FIBA)
 *   TTE game; W 2 L 1 forfeit 0 (ITTF) — CONFIRM            HOC quarter, PSO; 3/1/0 — a shoot-out win is a WIN (user, 2026-10-29)
 *   SQU game; W 2 L 0 (old OVR squash standings)            NET quarter, ET; 2/1/0
 *   TEN, PAD, TQB set; W 1 L 0 — CONFIRM                    RUG half, ET; 3/1/0
 *   VBL set; 3/3/2/1/0/0 by sets (FIVB)                     KAB half, 5R / GR; 2/1/0
 *   STK set; W 2 L 0                                        CRI innings, super over; W 2 tie 1 NR 1 L 0
 *   FB  half, ET, penalties; 3/1/0                          IHO period, OT, SO; 3 / OT·SO win 2 / OT·SO loss 1 / 0 — CONFIRM
 *   LBW end; 2/1/0     PET end; W 1 L 0 — CONFIRM           FLB period, OT, PSO; as ice hockey — CONFIRM
 *   BIL frame (pool: rack); W 1 L 0 — CONFIRM               ESP game; 3/2/1/0 by games (old OVR MLBB) — CONFIRM
 *   AQ  water polo: quarter, PSO; 3 / PSO win 2 / PSO loss 1 / 0 (old OVR)
 *   combat sports: round, no group points (knock-out)
 */
const BY_SPORT: Readonly<Record<string, MatchFormat>> = {
	BD: { period: "GAME", tiebreak: "BWF", points: { win: 1, draw: 0, loss: 0 } },
	TTE: { period: "GAME", points: { win: 2, draw: 0, loss: 1, forfeit: 0 } },
	SQU: { period: "GAME", points: { win: 2, draw: 0, loss: 0 } },
	TEN: { period: "SET", points: { win: 1, draw: 0, loss: 0 } },
	PAD: { period: "SET", points: { win: 1, draw: 0, loss: 0 } },
	TQB: { period: "SET", points: { win: 1, draw: 0, loss: 0 } },
	VBL: { period: "SET", points: { win: 3, draw: 0, loss: 0, by_sets: SETS_3_0 } },
	STK: { period: "SET", points: { win: 2, draw: 0, loss: 0 } },
	BIL: { period: "FRAME", points: { win: 1, draw: 0, loss: 0 } },
	LBW: { period: "END", points: { win: 2, draw: 1, loss: 0 } },
	PET: { period: "END", points: { win: 1, draw: 0, loss: 0 } },
	ESP: { period: "GAME", points: { win: 3, draw: 0, loss: 0, by_sets: SETS_2_0 } },
	FB: { period: "HALF", extra: "ET", shootout: "PENS", points: { win: 3, draw: 1, loss: 0 } },
	BKB: { period: "QUARTER", extra: "OT", points: { win: 2, draw: 0, loss: 1, forfeit: 0 } },
	HOC: { period: "QUARTER", shootout: "PSO", points: { win: 3, draw: 1, loss: 0 } },
	NET: { period: "QUARTER", extra: "ET", points: { win: 2, draw: 1, loss: 0 } },
	RUG: { period: "HALF", extra: "ET", points: { win: 3, draw: 1, loss: 0 } },
	KAB: { period: "HALF", extra: "ET", extra_labels: ["5R", "GR"], points: { win: 2, draw: 1, loss: 0 } },
	CRI: { period: "INNINGS", shootout: "SUPER_OVER", points: { win: 2, draw: 1, loss: 0, no_result: 1 } },
	IHO: { period: "PERIOD", extra: "OT", shootout: "SO", points: { win: 3, draw: 0, loss: 0, extra_win: 2, extra_loss: 1 } },
	FLB: { period: "PERIOD", extra: "OT", shootout: "PSO", points: { win: 3, draw: 0, loss: 0, extra_win: 2, extra_loss: 1 } },
	AQ: { period: "QUARTER", shootout: "PSO", points: { win: 3, draw: 0, loss: 0, extra_win: 2, extra_loss: 1 } },
	BOX: { period: "ROUND" },
	JUD: { period: "ROUND" },
	KTE: { period: "ROUND" },
	MUA: { period: "ROUND" },
	SIL: { period: "ROUND" },
	TKD: { period: "ROUND" },
	WSH: { period: "ROUND" },
	FEN: { period: "PERIOD" },
	// Head-to-head units of ranked sports (phase 5): an archery match (ends, a shoot-off), a
	// cycling sprint match (rides), a bowling stepladder (games), sailing team racing (races).
	ARC: { period: "END", shootout: "SO" },
	CYC: { period: "RACE" },
	BOW: { period: "GAME" },
	SAL: { period: "RACE" },
};

/** A fencing team match is a relay of nine legs (FIE): its periods are LEGS. */
const FENCING_TEAM: MatchFormat = { period: "LEG" };
/** Fencing's codes, as `boutProfileFor` reads them (its alias included). */
const FENCING_CODES: ReadonlySet<string> = new Set(["FEN", "FENCING"]);

/** The format of a TEAM event of the sport where it reads differently from the individual one (fencing's relay legs); else `matchFormatFor`. */
export function teamMatchFormatFor(sportCode: string | null | undefined, disciplineCode?: string | null): MatchFormat {
	if (FENCING_CODES.has((sportCode ?? "").trim().toUpperCase())) return FENCING_TEAM;
	return matchFormatFor(sportCode, disciplineCode);
}

/** `<sport>/<discipline>` overrides — only where the discipline plays differently. */
const BY_DISCIPLINE: Readonly<Record<string, MatchFormat>> = {
	"VBL/BVB": { period: "SET", points: { win: 3, draw: 0, loss: 0, by_sets: SETS_2_0 } },
	"VBL/VBB": { period: "SET", points: { win: 3, draw: 0, loss: 0, by_sets: SETS_2_0 } },
	"BKB/B3X3": { period: "PERIOD", extra: "OT", points: { win: 2, draw: 0, loss: 1, forfeit: 0 } },
	"HOC/HK5": { period: "PERIOD", shootout: "PSO", points: { win: 3, draw: 1, loss: 0 } },
	"BIL/POOL": { period: "RACK", points: { win: 1, draw: 0, loss: 0 } },
	"FB/FUT": { period: "HALF", extra: "ET", shootout: "PENS", points: { win: 3, draw: 1, loss: 0 } },
};

/** A sport nobody has described yet: neutral words, no points table. */
const FALLBACK: MatchFormat = { period: "GAME" };

/** The match format of a sport (and discipline, when it has its own). Codes compare case-blind. */
export function matchFormatFor(sportCode: string | null | undefined, disciplineCode?: string | null): MatchFormat {
	const sport = (sportCode ?? "").trim().toUpperCase();
	const disc = (disciplineCode ?? "").trim().toUpperCase();
	return BY_DISCIPLINE[`${sport}/${disc}`] ?? BY_SPORT[sport] ?? FALLBACK;
}

/** The group tie-break of a sport (and discipline). */
export const groupTiebreakFor = (sportCode: string | null | undefined, disciplineCode?: string | null): GroupTiebreak =>
	matchFormatFor(sportCode, disciplineCode).tiebreak ?? "SCORE";

/** The sport codes whose group tables follow BWF — what the API's standings statement reads (sport-level only: no discipline differs). */
export const BWF_TIEBREAK_SPORT_CODES: readonly string[] = Object.entries(BY_SPORT)
	.filter(([, f]) => f.tiebreak === "BWF")
	.map(([code]) => code);

/** One side of a group table, as the BWF tie-break reads it. Differences are for − against. */
export interface GroupTally {
	id: string;
	/** null = unranked (no counted match). */
	points: number | null;
	/** Matches (a tie's rubbers) or, for an individual match, games. */
	scoreDiff: number;
	gamesDiff: number;
	ralliesDiff: number;
	/** The referee's order among sides the rule leaves level (drawing of lots); null = none. */
	tieOrder: number | null;
}

/**
 * The BWF group order (`GROUP_TIEBREAKS`), as positions: `rank` is shared by
 * sides the rule leaves level. `wins(a, b)` = how many times `a` beat `b` in
 * the group. The API's standings statement and the portal's table apply the
 * same steps; this is their reference (unit tests pin both to it).
 */
export function bwfGroupRanks(rows: readonly GroupTally[], wins: (a: string, b: string) => number): Map<string, number> {
	const h2h = (group: readonly GroupTally[]) => (r: GroupTally) => group.reduce((n, o) => (o.id === r.id ? n : n + wins(r.id, o.id) - wins(o.id, r.id)), 0);
	const key = (r: GroupTally, level: readonly GroupTally[], sub: readonly GroupTally[]): number[] => [
		-(r.points ?? 0),
		level.length === 2 ? -h2h(level)(r) : 0,
		-r.scoreDiff,
		-r.gamesDiff,
		-r.ralliesDiff,
		level.length >= 3 && sub.length === 2 ? -h2h(sub)(r) : 0,
		r.tieOrder ?? Number.MAX_SAFE_INTEGER,
	];
	const ranked = rows.filter((r) => r.points !== null);
	const level = (r: GroupTally) => ranked.filter((o) => o.points === r.points);
	const sub = (r: GroupTally) => level(r).filter((o) => o.scoreDiff === r.scoreDiff && o.gamesDiff === r.gamesDiff && o.ralliesDiff === r.ralliesDiff);
	const keys = new Map(ranked.map((r) => [r.id, key(r, level(r), sub(r))]));
	const cmp = (a: number[], b: number[]) => {
		for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return (a[i] as number) - (b[i] as number);
		return 0;
	};
	const sorted = [...ranked].sort((a, b) => cmp(keys.get(a.id) as number[], keys.get(b.id) as number[]));
	const out = new Map<string, number>();
	sorted.forEach((r, i) => {
		const prev = sorted[i - 1];
		out.set(r.id, prev && cmp(keys.get(prev.id) as number[], keys.get(r.id) as number[]) === 0 ? (out.get(prev.id) as number) : i + 1);
	});
	return out;
}

/**
 * One side's group points for a finished match, by the format's table: the
 * sets / games score when the table keys on it, a forfeit when the side lost
 * by an invalid result, an overtime / shoot-out result when the table tells
 * those apart. Null when the sport has no points table.
 */
export function matchPointsOf(
	points: MatchPoints | undefined,
	side: { won: boolean; drew: boolean; forfeited: boolean; afterExtra: boolean; setsFor?: number; setsAgainst?: number; noResult?: boolean },
): number | null {
	if (!points) return null;
	if (side.noResult) return points.no_result ?? points.draw;
	if (side.drew) return points.draw;
	if (side.forfeited && !side.won) return points.forfeit ?? points.loss;
	if (points.by_sets && side.setsFor !== undefined && side.setsAgainst !== undefined) {
		const p = points.by_sets[`${side.setsFor}-${side.setsAgainst}`];
		if (p !== undefined) return p;
	}
	if (side.afterExtra) return side.won ? (points.extra_win ?? points.win) : (points.extra_loss ?? points.loss);
	return side.won ? points.win : points.loss;
}

// ─── Win methods ─────────────────────────────────────────────────────────────

/**
 * Every head-to-head win method a slot's `detail.win_by` may hold, with the
 * English the results pages print. OVR's combat screen offers each sport its
 * own subset (`apps/ovr/src/families/bout/rules.ts`). A code not listed here
 * is printed as it is.
 */
export const WIN_METHOD_LABELS = {
	PTF: "Final score",
	PTG: "Point gap",
	GDP: "Golden point",
	SUP: "Superiority",
	RSC: "Referee stops contest",
	WDR: "Withdrawal",
	DSQ: "Disqualification",
	PUN: "Punitive declaration",
	WP: "Win on points",
	RSCI: "Referee stops contest, injury",
	KO: "Knockout",
	ABD: "Abandonment",
	WO: "Walkover",
	IPPON: "Ippon",
	WAZARI: "Waza-ari awasete ippon",
	HSK: "Hansoku-make",
	FUS: "Fusen-gachi",
	KIK: "Kiken-gachi",
	PTS: "Points",
	SENSHU: "Senshu",
	HANTEI: "Hantei",
	// ── Combat port (old OVR WSL-OVR-X) ───────────────────────────────────
	/** Taekwondo: disqualification for misconduct (WT; old OVR "Disqual. Behavior"). */
	DQB: "Disqualification for misconduct",
	/** Muay (IFMA; old OVR meet-mti.tsx). */
	RSCS: "Referee stops contest, outclassed",
	RSCH: "Referee stops contest, head blow",
	RSCB: "Referee stops contest, body blow",
	/** Wushu sanda (IWUF; old OVR dsc-wsu-wss.tsx). */
	WPD: "Win by point difference",
	WKO: "Technical knockout",
	WIS: "Opponent injured or sick",
	WTV: "Technical victory",
	WDF: "Opponent's default",
	/** Judo (IJF 2025 brought yuko back; old OVR meet-jud.tsx). */
	YUKO: "Yuko",
	/** Fencing: a level score at time decided by priority (FIE). */
	PRI: "Priority",
	/** Pencak silat (PERSILAT; old OVR "Win by Technique") — CONFIRM with the TD. */
	WT: "Win by technique",
	/** Pencak silat: absolute win, the opponent cannot continue (Menang Mutlak) — CONFIRM with the TD. */
	ABS: "Absolute win",
} as const satisfies Record<string, string>;
export type WinMethodCode = keyof typeof WIN_METHOD_LABELS;
export const WIN_METHOD_CODES = Object.keys(WIN_METHOD_LABELS) as [WinMethodCode, ...WinMethodCode[]];

/**
 * Codes the predecessor stored for a method this list spells otherwise:
 * boxing's injury stoppage `RSC-I`, judo's `WAZA_ARI`, silat's `DQ`. Read
 * through `normalizeWinMethod` wherever a stored code is shown or compared.
 */
export const WIN_METHOD_ALIASES: Readonly<Record<string, WinMethodCode>> = { "RSC-I": "RSCI", WAZA_ARI: "WAZARI", DQ: "DSQ" };

/** A stored win method in this list's spelling (an old alias → its code); any other code as it is. */
export function normalizeWinMethod(code: string): string {
	return WIN_METHOD_ALIASES[code] ?? code;
}

/** The printed name of a win method (an old alias reads as its code's); the code itself when it has none. */
export function winMethodLabel(code: string): string {
	return (WIN_METHOD_LABELS as Readonly<Record<string, string>>)[normalizeWinMethod(code)] ?? code;
}
